import type { WebTestRun } from '@blume/web-test-model'
import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { aggregateProgress, filterRuns, sortRuns, type StatusFilter } from '@/logic'

import { EmptyState, GroupCard, PageTitle, RunList, SectionLabel } from './parts'

type QuickFilter = 'all' | 'attention' | 'active' | 'passed'

const quickFilters: Array<{ id: QuickFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'active', label: 'Active' },
  { id: 'passed', label: 'Passed' }
]

function initialQuickFilter(): QuickFilter {
  const value = new URLSearchParams(window.location.search).get('runsView')
  return quickFilters.some((filter) => filter.id === value) ? (value as QuickFilter) : 'all'
}

function initialRunQuery(): string {
  return new URLSearchParams(window.location.search).get('runsQuery') ?? ''
}

function persistRunView(quick: QuickFilter, query: string): void {
  const url = new URL(window.location.href)
  if (quick === 'all') url.searchParams.delete('runsView')
  else url.searchParams.set('runsView', quick)
  if (query.trim() === '') url.searchParams.delete('runsQuery')
  else url.searchParams.set('runsQuery', query)
  window.history.replaceState(window.history.state, '', url)
}

function needsAttention(run: WebTestRun): boolean {
  return (
    run.state === 'terminal' &&
    run.status !== undefined &&
    run.status !== 'passed' &&
    run.status !== 'cancelled'
  )
}

export function RunsPage({
  runs,
  now,
  hasMore = false,
  loadingMore = false,
  onLoadMore
}: {
  runs: WebTestRun[]
  now: number
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?(): Promise<void>
}) {
  const [quick, setQuick] = useState<QuickFilter>(initialQuickFilter)
  const [query, setQuery] = useState(initialRunQuery)

  useEffect(() => persistRunView(quick, query), [quick, query])

  const progress = useMemo(() => aggregateProgress(runs), [runs])
  const searched = useMemo(() => {
    const status: StatusFilter =
      quick === 'passed' ? 'passed' : quick === 'active' ? 'running' : 'all'
    const base = filterRuns(runs, {
      query,
      status,
      savedView: quick === 'attention' ? 'needs-attention' : 'all',
      extra: {
        classification: '',
        target: '',
        origin: '',
        suite: '',
        scenario: '',
        branch: '',
        pullRequest: '',
        time: 'all'
      },
      now
    })
    return sortRuns(base, 'newest')
  }, [runs, quick, query, now])

  const attention = searched.filter(needsAttention)
  const active = searched.filter((run) => run.state !== 'terminal')
  const rest = searched.filter((run) => !needsAttention(run) && run.state === 'terminal')
  const grouped = quick === 'all' && query.trim() === ''

  return (
    <>
      <PageTitle
        title="Runs"
        subtitle="Every individual verifier execution, across local work and pull requests. Filter here to inspect what happened in one test run."
      />
      <p className="-mt-5 mb-8 text-sm text-muted-foreground">{progress.summary}</p>

      <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-3">
        <div
          role="group"
          aria-label="Filter runs"
          className="flex h-9 items-center gap-1 rounded-md bg-muted p-1"
        >
          {quickFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              aria-pressed={quick === filter.id}
              onClick={() => setQuick(filter.id)}
              className={cn(
                'h-7 rounded-sm px-3 text-[13px] font-medium whitespace-nowrap transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                quick === filter.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search scenario, branch, PR, author…"
          aria-label="Search runs"
          className="h-9 w-full sm:max-w-xs"
        />
      </div>

      {searched.length === 0 ? (
        <EmptyState>No runs match your current search and view.</EmptyState>
      ) : grouped ? (
        <div className="space-y-6">
          {attention.length > 0 ? (
            <section aria-label="Runs that need attention">
              <GroupCard header={<SectionLabel>Needs attention</SectionLabel>}>
                <RunList runs={attention} now={now} />
              </GroupCard>
            </section>
          ) : null}
          {active.length > 0 ? (
            <section aria-label="Active runs">
              <GroupCard header={<SectionLabel>Active</SectionLabel>}>
                <RunList runs={active} now={now} />
              </GroupCard>
            </section>
          ) : null}
          {rest.length > 0 ? (
            <section aria-label="Recent completed runs">
              <GroupCard header={<SectionLabel>Recent</SectionLabel>}>
                <RunList runs={rest} now={now} />
              </GroupCard>
            </section>
          ) : null}
        </div>
      ) : (
        <RunList runs={searched} now={now} />
      )}

      {hasMore && onLoadMore ? (
        <div className="mt-8 max-w-[46rem] border-t pt-6 text-center">
          <Button variant="outline" disabled={loadingMore} onClick={() => void onLoadMore()}>
            {loadingMore ? 'Loading older runs…' : 'Load older runs'}
          </Button>
        </div>
      ) : null}
    </>
  )
}
